import * as IF from "infrontjs";
import template from "./form.html";
import { validateScope, resetValidation, bindLiveClear } from "../general/form-validation";
import { _lcs, locale } from "../../i18n";

const SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Wizard step order: 0 Basis · 1 Kontakt · 2 Übersicht.
// (Darstellung/Branding is configured separately in the client-admin.)
const LAST_STEP = 2;

// All ISO 3166-1 alpha-2 country codes.
const COUNTRY_CODES = [
    "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
    "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV","BW","BY","BZ",
    "CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CR","CU","CV","CW","CX","CY","CZ",
    "DE","DJ","DK","DM","DO","DZ",
    "EC","EE","EG","EH","ER","ES","ET",
    "FI","FJ","FK","FM","FO","FR",
    "GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY",
    "HK","HM","HN","HR","HT","HU",
    "ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT",
    "JE","JM","JO","JP",
    "KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ",
    "LA","LB","LC","LI","LK","LR","LS","LT","LU","LV","LY",
    "MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ",
    "NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ",
    "OM",
    "PA","PE","PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY",
    "QA",
    "RE","RO","RS","RU","RW",
    "SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ",
    "TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ",
    "UA","UG","UM","US","UY","UZ",
    "VA","VC","VE","VG","VI","VN","VU",
    "WF","WS",
    "YE","YT",
    "ZA","ZM","ZW"
];

// Fallback set of European IANA time zones for browsers without
// Intl.supportedValuesOf (otherwise we derive the full list at runtime).
const FALLBACK_EU_TIMEZONES = [
    "Europe/Amsterdam","Europe/Andorra","Europe/Athens","Europe/Belgrade","Europe/Berlin",
    "Europe/Bratislava","Europe/Brussels","Europe/Bucharest","Europe/Budapest","Europe/Chisinau",
    "Europe/Copenhagen","Europe/Dublin","Europe/Gibraltar","Europe/Helsinki","Europe/Istanbul",
    "Europe/Kyiv","Europe/Lisbon","Europe/Ljubljana","Europe/London","Europe/Luxembourg",
    "Europe/Madrid","Europe/Malta","Europe/Minsk","Europe/Monaco","Europe/Moscow","Europe/Oslo",
    "Europe/Paris","Europe/Prague","Europe/Riga","Europe/Rome","Europe/Sarajevo","Europe/Sofia",
    "Europe/Stockholm","Europe/Tallinn","Europe/Tirane","Europe/Vaduz","Europe/Vienna",
    "Europe/Vilnius","Europe/Warsaw","Europe/Zagreb","Europe/Zurich"
];

/**
 * <accommodation-form> — modal form to create or edit the basic data of an
 * accommodation. Used by the AccommodationList state (and reusable elsewhere).
 *
 * Usage:
 *   el.openCreate( existingSlugs )                  // create mode
 *   el.openEdit( accommodation, existingSlugs )     // edit mode
 *
 * On a successful save it dispatches a bubbling "accommodation-saved" event
 * (detail: { mode, accommodation }) so the host can refresh its list.
 *
 * Slug uniqueness is checked client-side against `existingSlugs` (and the
 * backend still enforces it, surfaced as a 409).
 */
class AccommodationForm extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;

        this._mode = "create";
        this._id = null;
        this._originalSlug = null;
        this._existingSlugs = new Set();
        this._existingData = {};
        this._step = 0;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="acc-modal"]' );
        this._form = this.querySelector( '[data-id="acc-form-el"]' );
        this._title = this.querySelector( '[data-id="acc-title"]' );
        this._submit = this.querySelector( '[data-id="acc-submit"]' );
        this._submitLabel = this.querySelector( '[data-id="acc-submit-label"]' );
        this._error = this.querySelector( '[data-id="acc-error"]' );
        this._ownerField = this.querySelector( '[data-id="acc-owner-field"]' );
        this._ownerSelect = this.querySelector( "#acc-owner" );

        // Wizard navigation elements.
        this._stepItems = Array.from( this.querySelectorAll( "[data-step-indicator]" ) );
        this._panels = Array.from( this.querySelectorAll( "[data-step-panel]" ) );
        this._back = this.querySelector( '[data-id="acc-back"]' );
        this._next = this.querySelector( '[data-id="acc-next"]' );
        this._summary = this.querySelector( '[data-id="acc-summary"]' );

        // Static, large option lists — built once.
        this._populateTimezones();
        this._populateCountries();
    }

    bind()
    {
        this.querySelector( '[data-id="acc-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="acc-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="acc-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._form.addEventListener( "submit", ( e ) => this.onSubmit( e ) );

        // Wizard navigation.
        this._back.addEventListener( "click", () => this._goToStep( this._step - 1 ) );
        this._next.addEventListener( "click", () => this._attemptGoTo( this._step + 1 ) );
        this._stepItems.forEach( ( item, i ) =>
            item.addEventListener( "click", () => this._attemptGoTo( i ) )
        );

        // Clear a field's error as soon as the user edits it.
        bindLiveClear( this._form );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    openCreate( existingSlugs = [], clientUsers = [] )
    {
        this._mode = "create";
        this._id = null;
        this._originalSlug = null;
        this._existingData = {};
        this._existingAddressName = null;
        this._setExistingSlugs( existingSlugs );

        this._clearError();
        this._form.reset();
        this._title.textContent = _lcs( "Neue Unterkunft anlegen" );
        this._submitLabel.textContent = _lcs( "Anlegen" );

        // Owner select: shown, enabled and required on create.
        this._populateOwners( clientUsers );
        this._ownerField.hidden = false;
        this._ownerSelect.disabled = false;
        this._ownerSelect.value = "";

        // Defaults. Type: "guesthouse" is the only enabled option (see form.html).
        this._val( "acc-type", "guesthouse" );
        this._val( "acc-region", "saechsische_schweiz" );
        this._val( "acc-status", "draft" );
        this._val( "acc-timezone", this._defaultTimezone() );
        this._val( "acc-countryCode", this._defaultCountryCode() );

        resetValidation( this._form );
        this._modal.hidden = false;
        this._goToStep( 0 );
        this._field( "acc-name" ).focus();
    }

    openEdit( accommodation, existingSlugs = [], ownerLabel = "" )
    {
        this._mode = "edit";
        this._id = accommodation.id;
        this._originalSlug = accommodation.slug ? String( accommodation.slug ).toLowerCase() : null;
        this._existingData = ( accommodation.data && typeof accommodation.data === "object" ) ? { ...accommodation.data } : {};
        this._setExistingSlugs( existingSlugs );

        this._clearError();
        this._form.reset();
        this._title.textContent = _lcs( "Unterkunft bearbeiten" );
        this._submitLabel.textContent = _lcs( "Aktualisieren" );

        // Owner is set at creation only — shown read-only (disabled) here.
        this._showOwnerReadonly( accommodation.ownerUserId, ownerLabel );

        this._val( "acc-name", accommodation.name || "" );
        this._val( "acc-slug", accommodation.slug || "" );
        this._val( "acc-type", accommodation.type || "guesthouse" );
        this._val( "acc-region", accommodation.region || "saechsische_schweiz" );
        this._val( "acc-status", this._normalizedStatus( accommodation.status ) );
        this._val( "acc-www", this._existingData.www || "" );
        this._val( "acc-email", this._existingData.email || "" );
        this._val( "acc-phone", this._existingData.phone || "" );

        // Timezone select — keep a non-European saved value selectable.
        const tz = accommodation.timezone || this._defaultTimezone();
        this._ensureOption( this._field( "acc-timezone" ), tz, tz );
        this._field( "acc-timezone" ).value = tz;

        const addr = ( accommodation.address && typeof accommodation.address === "object" ) ? accommodation.address : {};
        this._existingAddressName = addr.name || null;
        this._val( "acc-street", addr.street || "" );
        this._val( "acc-postalCode", addr.postalCode || "" );
        this._val( "acc-city", addr.city || "" );

        // Country select — fall back to the default when none was stored.
        const cc = ( addr.countryCode ? String( addr.countryCode ).toUpperCase() : "" ) || this._defaultCountryCode();
        this._ensureOption( this._field( "acc-countryCode" ), cc, cc );
        this._field( "acc-countryCode" ).value = cc;

        resetValidation( this._form );
        this._modal.hidden = false;
        this._goToStep( 0 );
        this._field( "acc-name" ).focus();
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
        this._form.reset();
    }

    /* -------------------------------- Submit -------------------------------- */

    async onSubmit( e )
    {
        e.preventDefault();
        this._clearError();

        // Pressing Enter can submit from any step — only the final step saves;
        // anywhere else it just advances the wizard.
        if ( this._step !== LAST_STEP )
        {
            this._attemptGoTo( this._step + 1 );
            return;
        }

        const name = this._val( "acc-name" ).trim();
        const slug = this._val( "acc-slug" ).trim().toLowerCase();
        const type = this._val( "acc-type" );
        const region = this._val( "acc-region" );
        const status = this._val( "acc-status" );
        const timezone = this._val( "acc-timezone" ).trim() || AccommodationForm.browserTimezone();
        const www = this._val( "acc-www" ).trim();
        const email = this._val( "acc-email" ).trim();
        const phone = this._val( "acc-phone" ).trim();

        const street = this._val( "acc-street" ).trim();
        const postalCode = this._val( "acc-postalCode" ).trim();
        const city = this._val( "acc-city" ).trim();
        const countryCode = this._val( "acc-countryCode" ).trim().toUpperCase();

        // Re-validate every input step; jump back to the first one with an error.
        for ( const step of [ 0, 1 ] )
        {
            if ( !this._validateStep( step ) )
            {
                this._goToStep( step );
                return;
            }
        }

        // Owner is required on create only.
        const ownerUserId = this._mode === "create" ? this._val( "acc-owner" ) : null;

        // Street, postcode and town are required (the API geocodes them —
        // the origin of the guest-app's distances); keep the name if one is stored.
        const addressPayload = { street, postalCode, city };
        if ( countryCode ) addressPayload.countryCode = countryCode;
        if ( this._existingAddressName ) addressPayload.name = this._existingAddressName;

        // Preserve any other keys already present in data; manage only the
        // contact keys (www / email / phone) here.
        const data = { ...this._existingData };
        if ( www ) data.www = www;
        else delete data.www;
        if ( email ) data.email = email;
        else delete data.email;
        if ( phone ) data.phone = phone;
        else delete data.phone;
        const dataPayload = Object.keys( data ).length > 0 ? data : null;

        const payload = {
            name,
            slug,
            type,
            region,
            status,
            timezone,
            address: addressPayload,
            data: dataPayload
        };

        this._setSubmitting( true );
        try
        {
            let result;
            if ( this._mode === "edit" )
            {
                ( { result } = await this.app.api.patch( "/accommodation/" + this._id, payload ) );
                if ( !result || +result.status !== 200 )
                {
                    throw new Error( "Unexpected update response." );
                }
            }
            else
            {
                // Owner (a client user) is selected in the form and required by
                // the admin create endpoint.
                payload.ownerUserId = ownerUserId;
                ( { result } = await this.app.api.post( "/accommodation", payload ) );
                if ( !result || +result.status !== 201 )
                {
                    throw new Error( "Unexpected create response." );
                }
            }

            const saved = result.data || null;
            this.app.view.toast(
                this._mode === "edit"
                    ? _lcs( "Unterkunft „{name}“ wurde aktualisiert.", { name } )
                    : _lcs( "Unterkunft „{name}“ wurde angelegt.", { name } ),
                "success"
            );
            this.close();
            this.dispatchEvent( new CustomEvent( "accommodation-saved", {
                bubbles: true,
                detail: { mode: this._mode, accommodation: saved }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._errorForStatus( +err?.status ) );
        }
        finally
        {
            this._setSubmitting( false );
        }
    }

    /* ------------------------------- Helpers -------------------------------- */

    static browserTimezone()
    {
        try
        {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
        }
        catch ( e )
        {
            return "Europe/Berlin";
        }
    }

    // All European IANA time zones (derived at runtime when available).
    static europeanTimezones()
    {
        try
        {
            if ( typeof Intl.supportedValuesOf === "function" )
            {
                const list = Intl.supportedValuesOf( "timeZone" ).filter( ( tz ) => tz.startsWith( "Europe/" ) );
                if ( list.length > 0 )
                {
                    return list;
                }
            }
        }
        catch ( e ) { /* fall through */ }
        return FALLBACK_EU_TIMEZONES;
    }

    // 2-letter country code from the browser locale; "DE" when unresolved.
    static browserCountryCode()
    {
        try
        {
            const locale = navigator.language || Intl.DateTimeFormat().resolvedOptions().locale || "";
            let region = null;
            try { region = new Intl.Locale( locale ).region; } catch ( e ) { /* noop */ }
            if ( !region )
            {
                const m = String( locale ).match( /[-_]([A-Za-z]{2})\b/ );
                if ( m ) region = m[ 1 ];
            }
            region = region ? region.toUpperCase() : null;
            return ( region && COUNTRY_CODES.includes( region ) ) ? region : "DE";
        }
        catch ( e )
        {
            return "DE";
        }
    }

    _defaultTimezone()
    {
        const tz = AccommodationForm.browserTimezone();
        return AccommodationForm.europeanTimezones().includes( tz ) ? tz : "Europe/Berlin";
    }

    _defaultCountryCode()
    {
        return AccommodationForm.browserCountryCode();
    }

    // Status is a two-value enum (draft | published). Legacy values from
    // before the reduction ("active" etc.) map onto it defensively.
    _normalizedStatus( status )
    {
        const key = String( status || "" ).toLowerCase();
        if ( key === "published" || key === "active" )
        {
            return "published";
        }
        return "draft";
    }

    _populateTimezones()
    {
        const sel = this._field( "acc-timezone" );
        sel.innerHTML = "";
        AccommodationForm.europeanTimezones().forEach( ( tz ) =>
        {
            const opt = document.createElement( "option" );
            opt.value = tz;
            opt.textContent = tz;
            sel.appendChild( opt );
        } );
    }

    _populateCountries()
    {
        const sel = this._field( "acc-countryCode" );
        sel.innerHTML = "";

        // Country names come from the platform, not the dictionary: Intl already
        // knows all 249 of them in every language we could ship.
        let names = null;
        try { names = new Intl.DisplayNames( [ locale() ], { type: "region" } ); } catch ( e ) { /* noop */ }

        const options = COUNTRY_CODES.map( ( code ) =>
        {
            let label = code;
            if ( names )
            {
                try { label = ( names.of( code ) || code ) + " (" + code + ")"; } catch ( e ) { /* keep code */ }
            }
            return { code, label };
        } );
        options.sort( ( a, b ) => a.label.localeCompare( b.label, locale() ) );

        options.forEach( ( o ) =>
        {
            const opt = document.createElement( "option" );
            opt.value = o.code;
            opt.textContent = o.label;
            sel.appendChild( opt );
        } );
    }

    // Add an option to a select if it isn't already present (used in edit mode
    // for saved values outside the standard lists).
    _ensureOption( select, value, label )
    {
        if ( !select || !value ) return;
        const exists = Array.from( select.options ).some( ( o ) => o.value === value );
        if ( !exists )
        {
            const opt = document.createElement( "option" );
            opt.value = value;
            opt.textContent = label || value;
            select.appendChild( opt );
        }
    }

    _populateOwners( clientUsers )
    {
        const sel = this._ownerSelect;
        sel.innerHTML = "";

        const list = Array.isArray( clientUsers ) ? clientUsers : [];

        const placeholder = document.createElement( "option" );
        placeholder.value = "";
        placeholder.textContent = list.length
            ? _lcs( "Bitte wählen …" )
            : _lcs( "Keine Client-Benutzer verfügbar" );
        sel.appendChild( placeholder );

        list.forEach( ( u ) =>
        {
            const opt = document.createElement( "option" );
            opt.value = String( u.id );
            const name = ( u.data && u.data.name ) ? String( u.data.name ) : "";
            const email = u.email || "";
            opt.textContent = name ? ( email ? name + " (" + email + ")" : name ) : ( email || String( u.id ) );
            sel.appendChild( opt );
        } );
    }

    // Edit mode: render the owner as a single, disabled option (the owner is
    // assigned at creation and cannot be changed here).
    _showOwnerReadonly( ownerUserId, label )
    {
        const sel = this._ownerSelect;
        sel.innerHTML = "";

        const value = ownerUserId != null ? String( ownerUserId ) : "";
        const opt = document.createElement( "option" );
        opt.value = value;
        opt.textContent = ( label && String( label ).trim() ) || value || "—";
        sel.appendChild( opt );

        sel.value = value;
        sel.disabled = true;
        this._ownerField.hidden = false;
    }

    _setExistingSlugs( slugs )
    {
        this._existingSlugs = new Set(
            ( Array.isArray( slugs ) ? slugs : [] ).map( ( s ) => String( s || "" ).toLowerCase() )
        );
    }

    /* ------------------------------- Wizard --------------------------------- */

    // Show a given step, updating panels, the step indicator and the footer
    // buttons. The summary is (re)built whenever the last step is shown.
    _goToStep( step )
    {
        this._step = Math.max( 0, Math.min( LAST_STEP, step ) );

        this._panels.forEach( ( panel, i ) => { panel.hidden = ( i !== this._step ); } );
        this._stepItems.forEach( ( item, i ) =>
        {
            item.classList.toggle( "is-active", i === this._step );
            item.classList.toggle( "is-done", i < this._step );
        } );

        const isLast = this._step === LAST_STEP;
        this._back.hidden = this._step === 0;
        this._next.hidden = isLast;
        this._submit.hidden = !isLast;

        if ( isLast )
        {
            this._renderSummary();
        }
        this._clearError();
    }

    // Navigate to a target step. Going backwards is always allowed; going
    // forward validates every step in between and stops at the first invalid one.
    _attemptGoTo( target )
    {
        target = Math.max( 0, Math.min( LAST_STEP, target ) );
        if ( target > this._step )
        {
            for ( let step = this._step; step < target; step++ )
            {
                if ( !this._validateStep( step ) )
                {
                    this._goToStep( step );
                    return;
                }
            }
        }
        this._goToStep( target );
    }

    // Validate a single step via the native Constraint Validation API. Custom
    // rules that markup can't express are applied first via setCustomValidity,
    // then validateScope() marks invalid fields and shows hints. Returns true
    // when the step (or a step without its own fields) is valid.
    _validateStep( step )
    {
        const panel = this._panels[ step ];
        if ( !panel ) return true;

        this._applyCustomConstraints( step );
        return validateScope( panel );
    }

    // Set custom validity for the rules HTML constraints can't express. The
    // slug's format and uniqueness are checked here; everything else (required,
    // type=email / type=url) is handled natively by the markup.
    _applyCustomConstraints( step )
    {
        if ( step === 0 )
        {
            const slugEl = this._field( "acc-slug" );
            const slug = slugEl.value.trim().toLowerCase();
            let msg = "";
            if ( slug )
            {
                if ( !SLUG_RE.test( slug ) || slug.length > 63 )
                {
                    msg = _lcs( "Ungültiger Slug. Erlaubt: Kleinbuchstaben, Ziffern, Bindestriche." );
                }
                else if ( this._existingSlugs.has( slug ) && slug !== this._originalSlug )
                {
                    msg = _lcs( "Dieser Slug ist bereits vergeben." );
                }
            }
            slugEl.setCustomValidity( msg );
        }
    }

    // Build the read-only review shown on the last step.
    _renderSummary()
    {
        if ( !this._summary ) return;

        const rows = [];
        const add = ( label, value ) =>
        {
            const v = ( value === undefined || value === null ) ? "" : String( value ).trim();
            rows.push( { label, value: v || "—" } );
        };

        add( _lcs( "Name" ), this._val( "acc-name" ) );
        add( _lcs( "Typ" ), this._selectText( "acc-type" ) );
        add( _lcs( "Region" ), this._selectText( "acc-region" ) );
        add( _lcs( "Status" ), this._selectText( "acc-status" ) );
        const slug = this._val( "acc-slug" ).trim().toLowerCase();
        add( _lcs( "Slug" ), slug ? "https://" + slug + ".belturo.app" : "" );
        add( _lcs( "Eigentümer" ), this._selectText( "acc-owner" ) );
        add( _lcs( "Zeitzone" ), this._val( "acc-timezone" ) );
        add( _lcs( "Webseite" ), this._val( "acc-www" ) );
        add( _lcs( "E-Mail" ), this._val( "acc-email" ) );
        add( _lcs( "Telefon" ), this._val( "acc-phone" ) );

        const addressLine = [
            this._val( "acc-street" ).trim(),
            [ this._val( "acc-postalCode" ).trim(), this._val( "acc-city" ).trim() ].filter( Boolean ).join( " " ),
            this._val( "acc-countryCode" ).trim().toUpperCase()
        ].filter( Boolean ).join( ", " );
        add( _lcs( "Adresse" ), addressLine );

        this._summary.innerHTML = "";
        rows.forEach( ( row ) =>
        {
            const dt = document.createElement( "dt" );
            dt.textContent = row.label;
            const dd = document.createElement( "dd" );
            dd.textContent = row.value;
            this._summary.appendChild( dt );
            this._summary.appendChild( dd );
        } );
    }

    // Visible label of a <select>'s current option (falls back to its value).
    _selectText( id )
    {
        const el = this._field( id );
        if ( !el ) return "";
        const opt = el.options[ el.selectedIndex ];
        return opt ? opt.textContent : el.value;
    }

    /* ------------------------------- Helpers -------------------------------- */

    _field( id )
    {
        return this.querySelector( "#" + id );
    }

    _val( id, value )
    {
        const el = this._field( id );
        if ( value !== undefined )
        {
            if ( el ) el.value = value;
            return value;
        }
        return el ? el.value : "";
    }

    _errorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Unterkunft wurde nicht gefunden." );
            case 409: return _lcs( "Dieser Slug ist bereits vergeben." );
            default:  return this._mode === "edit"
                ? _lcs( "Aktualisieren fehlgeschlagen. Bitte versuche es später erneut." )
                : _lcs( "Anlegen fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _setSubmitting( on )
    {
        if ( !this._submit ) return;
        this._submit.disabled = on;
        this._submit.setAttribute( "aria-busy", String( on ) );
    }

    _showError( message )
    {
        if ( !this._error ) return;
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        if ( !this._error ) return;
        this._error.textContent = "";
        this._error.hidden = true;
    }
}

customElements.define( "accommodation-form", AccommodationForm );
